import { useState, useEffect, useCallback, useRef } from 'react';
import { usePlants } from '../../hooks/usePlants';
import { useAuth } from '../../context/AuthContext';
import { isExpandedCareInfo } from '../../types/careExpanded';
import { generateCareInfo } from '../../services/api';
import { updatePlantCareInfo } from '../../services/plants';
import type { Plant, CareInfo } from '../../types/plant';

interface UpgradeError {
  plantId: string;
  plantName: string;
  error: string;
}

interface BulkUpgradeStatus {
  state: 'idle' | 'running' | 'paused' | 'completed';
  totalPlants: number;
  completed: number;
  failed: number;
  currentPlant: string | null;
  errors: UpgradeError[];
  rateLimitResetAt: number | null;
}

const DELAY_BETWEEN_CALLS_MS = 500;

/**
 * Parse retry minutes from rate limit error message.
 * Expects format: "Too many requests. Please try again in X minutes."
 */
function parseRetryMinutes(errorMessage: string): number | null {
  const match = errorMessage.match(/try again in (\d+) minutes?/i);
  if (match && match[1]) {
    return parseInt(match[1], 10);
  }
  return null;
}

/**
 * Format seconds as MM:SS
 */
function formatCountdown(totalSeconds: number): string {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
}

/**
 * Get plants eligible for v1 -> v2 care info upgrade.
 * Eligible: has v1 care info, not custom, has scientific name.
 */
function getEligiblePlants(plants: Plant[]): Plant[] {
  return plants.filter((plant) => {
    // Must have care info
    if (!plant.careInfo) return false;
    // Must not already be v2
    if (isExpandedCareInfo(plant.careInfo)) return false;
    // Must not be custom
    if (plant.isCustom) return false;
    // Must have scientific name for API call
    if (!plant.scientificName) return false;
    return true;
  });
}

export function BulkUpgradeSection() {
  const { user } = useAuth();
  const { plants } = usePlants();

  const [status, setStatus] = useState<BulkUpgradeStatus>({
    state: 'idle',
    totalPlants: 0,
    completed: 0,
    failed: 0,
    currentPlant: null,
    errors: [],
    rateLimitResetAt: null,
  });

  const [countdownSeconds, setCountdownSeconds] = useState<number>(0);
  const [isErrorsExpanded, setIsErrorsExpanded] = useState(false);

  // Track plants to process and current index
  const plantsToProcessRef = useRef<Plant[]>([]);
  const currentIndexRef = useRef<number>(0);
  const abortRef = useRef<boolean>(false);

  // Get eligible plants
  const eligiblePlants = getEligiblePlants(plants);
  const eligibleCount = eligiblePlants.length;

  // Countdown timer effect
  useEffect(() => {
    if (status.state !== 'paused' || !status.rateLimitResetAt) return;

    const interval = setInterval(() => {
      const now = Date.now();
      const remaining = Math.max(0, Math.ceil((status.rateLimitResetAt! - now) / 1000));
      setCountdownSeconds(remaining);

      if (remaining === 0) {
        // Auto-resume when countdown reaches 0
        setStatus((prev) => ({
          ...prev,
          state: 'running',
          rateLimitResetAt: null,
        }));
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [status.state, status.rateLimitResetAt]);

  // Process next plant
  const processNextPlant = useCallback(async () => {
    if (!user) return;

    const plantsToProcess = plantsToProcessRef.current;
    const currentIndex = currentIndexRef.current;

    // Check if we're done or aborted
    if (abortRef.current || currentIndex >= plantsToProcess.length) {
      setStatus((prev) => ({
        ...prev,
        state: 'completed',
        currentPlant: null,
      }));
      return;
    }

    const plant = plantsToProcess[currentIndex];

    // Defensive check (should never happen but TypeScript needs it)
    if (!plant) {
      currentIndexRef.current += 1;
      if (currentIndexRef.current < plantsToProcess.length) {
        setTimeout(processNextPlant, DELAY_BETWEEN_CALLS_MS);
      } else {
        setStatus((prev) => ({ ...prev, state: 'completed', currentPlant: null }));
      }
      return;
    }

    const plantName = plant.nickname || plant.commonName;

    // Update current plant
    setStatus((prev) => ({
      ...prev,
      currentPlant: plantName,
    }));

    try {
      // Call the API (scientificName is guaranteed by getEligiblePlants filter)
      const result = await generateCareInfo({
        scientificName: plant.scientificName!,
        commonName: plant.commonName,
      });

      // Update plant with new care info
      const updateResult = await updatePlantCareInfo(user.uid, plant.id, result.careInfo as CareInfo);
      if (!updateResult.success) {
        throw new Error(updateResult.error);
      }

      // Update status
      setStatus((prev) => ({
        ...prev,
        completed: prev.completed + 1,
      }));
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';

      // Check for rate limit error
      const retryMinutes = parseRetryMinutes(errorMessage);
      if (retryMinutes !== null) {
        // Pause with countdown
        const resetAt = Date.now() + retryMinutes * 60 * 1000;
        setStatus((prev) => ({
          ...prev,
          state: 'paused',
          rateLimitResetAt: resetAt,
          currentPlant: null,
        }));
        setCountdownSeconds(retryMinutes * 60);
        return; // Don't advance index, will retry this plant
      }

      // Other error: record and continue
      setStatus((prev) => ({
        ...prev,
        failed: prev.failed + 1,
        errors: [
          ...prev.errors,
          {
            plantId: plant.id,
            plantName,
            error: errorMessage,
          },
        ],
      }));
    }

    // Advance to next plant
    currentIndexRef.current += 1;

    // Delay before next call
    if (!abortRef.current && currentIndexRef.current < plantsToProcess.length) {
      setTimeout(processNextPlant, DELAY_BETWEEN_CALLS_MS);
    } else {
      // All done
      setStatus((prev) => ({
        ...prev,
        state: 'completed',
        currentPlant: null,
      }));
    }
  }, [user]);

  // Effect to continue processing when state changes to running
  useEffect(() => {
    if (status.state === 'running' && !status.rateLimitResetAt) {
      processNextPlant();
    }
  }, [status.state, status.rateLimitResetAt, processNextPlant]);

  // Start upgrade
  const handleStart = useCallback(() => {
    const toProcess = getEligiblePlants(plants);
    plantsToProcessRef.current = toProcess;
    currentIndexRef.current = 0;
    abortRef.current = false;

    setStatus({
      state: 'running',
      totalPlants: toProcess.length,
      completed: 0,
      failed: 0,
      currentPlant: null,
      errors: [],
      rateLimitResetAt: null,
    });
    setIsErrorsExpanded(false);
  }, [plants]);

  // Pause upgrade
  const handlePause = useCallback(() => {
    setStatus((prev) => ({
      ...prev,
      state: 'paused',
      currentPlant: null,
    }));
  }, []);

  // Resume upgrade
  const handleResume = useCallback(() => {
    setStatus((prev) => ({
      ...prev,
      state: 'running',
    }));
  }, []);

  // Cancel upgrade
  const handleCancel = useCallback(() => {
    abortRef.current = true;
    setStatus((prev) => ({
      ...prev,
      state: 'completed',
      currentPlant: null,
    }));
  }, []);

  // Retry failed plants
  const handleRetryFailed = useCallback(() => {
    const failedPlantIds = new Set(status.errors.map((e) => e.plantId));
    const failedPlants = plants.filter((p) => failedPlantIds.has(p.id));

    // Only retry plants that still need upgrading (haven't been manually upgraded since)
    const stillNeedsUpgrade = failedPlants.filter(
      (p) => p.careInfo && !isExpandedCareInfo(p.careInfo)
    );

    if (stillNeedsUpgrade.length === 0) {
      return;
    }

    plantsToProcessRef.current = stillNeedsUpgrade;
    currentIndexRef.current = 0;
    abortRef.current = false;

    setStatus({
      state: 'running',
      totalPlants: stillNeedsUpgrade.length,
      completed: 0,
      failed: 0,
      currentPlant: null,
      errors: [],
      rateLimitResetAt: null,
    });
    setIsErrorsExpanded(false);
  }, [plants, status.errors]);

  // Reset to idle state
  const handleDismiss = useCallback(() => {
    setStatus({
      state: 'idle',
      totalPlants: 0,
      completed: 0,
      failed: 0,
      currentPlant: null,
      errors: [],
      rateLimitResetAt: null,
    });
  }, []);

  // Progress percentage
  const progressPercent =
    status.totalPlants > 0
      ? Math.round(((status.completed + status.failed) / status.totalPlants) * 100)
      : 0;

  // If no eligible plants, show informational message
  if (eligibleCount === 0 && status.state === 'idle') {
    return (
      <section className="mb-8">
        <h2 className="section-header mb-4">Care Guide Upgrade</h2>
        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-status-green/10">
              <span className="material-symbols-outlined text-status-green" style={{ fontSize: 20 }} aria-hidden="true">
                check_circle
              </span>
            </div>
            <div>
              <p className="font-serif font-medium text-editorial-black">All plants up to date</p>
              <p className="text-sm font-serif text-gray-500 mt-1">
                All your plants already have the latest care guide format.
              </p>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="mb-8">
      <h2 className="section-header mb-4">Care Guide Upgrade</h2>
      <div className="rounded-lg border border-gray-200 bg-white p-4">
        {/* Description */}
        {status.state === 'idle' && (
          <div className="mb-4">
            <p className="font-serif text-editorial-black">
              Upgrade your care guides to the expanded format with detailed sections for
              light, watering, feeding, potting, and more.
            </p>
            <p className="text-sm font-serif text-gray-500 mt-2">
              <span className="font-medium text-primary">{eligibleCount}</span>{' '}
              {eligibleCount === 1 ? 'plant' : 'plants'} can be upgraded.
            </p>
          </div>
        )}

        {/* Progress section (when running, paused, or completed) */}
        {status.state !== 'idle' && (
          <div className="space-y-4">
            {/* Progress bar */}
            <div>
              <div className="flex justify-between text-sm font-sans mb-1">
                <span className="text-gray-500">
                  {status.completed + status.failed} of {status.totalPlants} processed
                </span>
                <span className="text-gray-500">{progressPercent}%</span>
              </div>
              <div className="h-2 w-full rounded-full bg-gray-200">
                <div
                  className="h-full rounded-full bg-primary transition-all duration-300"
                  style={{ width: `${progressPercent}%` }}
                  role="progressbar"
                  aria-valuenow={status.completed + status.failed}
                  aria-valuemax={status.totalPlants}
                  aria-label="Upgrade progress"
                />
              </div>
            </div>

            {/* Current plant indicator */}
            {status.state === 'running' && status.currentPlant && (
              <div
                className="flex items-center gap-2 text-sm font-serif text-gray-500"
                role="status"
                aria-live="polite"
              >
                <span className="material-symbols-outlined animate-spin text-primary" style={{ fontSize: 16 }} aria-hidden="true">
                  progress_activity
                </span>
                <span>Upgrading: {status.currentPlant}...</span>
              </div>
            )}

            {/* Rate limit countdown */}
            {status.state === 'paused' && status.rateLimitResetAt && (
              <div
                className="rounded-lg bg-warning/10 p-4"
                role="alert"
                aria-live="polite"
              >
                <div className="flex items-center gap-3">
                  <span className="material-symbols-outlined text-warning flex-shrink-0" style={{ fontSize: 20 }} aria-hidden="true">
                    schedule
                  </span>
                  <div>
                    <p className="font-sans font-medium text-editorial-black">Rate limit reached</p>
                    <p className="text-sm font-serif text-gray-500">
                      Resuming automatically in{' '}
                      <span className="font-mono font-semibold text-warning">
                        {formatCountdown(countdownSeconds)}
                      </span>
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Manual pause indicator */}
            {status.state === 'paused' && !status.rateLimitResetAt && (
              <p className="text-sm font-serif text-gray-500">Upgrade paused</p>
            )}

            {/* Completion summary */}
            {status.state === 'completed' && (
              <div className="space-y-3">
                {/* Success count */}
                <div className="flex items-center gap-2">
                  <span className="material-symbols-outlined text-status-green" style={{ fontSize: 20 }} aria-hidden="true">
                    check_circle
                  </span>
                  <span className="font-serif text-editorial-black">
                    {status.completed} upgraded successfully
                  </span>
                </div>

                {/* Failed count with expandable errors */}
                {status.failed > 0 && (
                  <div>
                    <button
                      onClick={() => setIsErrorsExpanded(!isErrorsExpanded)}
                      className="flex w-full items-center justify-between rounded-lg bg-danger/5 px-3 py-2 text-left hover:bg-danger/10 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-danger"
                      aria-expanded={isErrorsExpanded}
                      aria-controls="error-list"
                    >
                      <div className="flex items-center gap-2">
                        <span className="material-symbols-outlined text-danger" style={{ fontSize: 20 }} aria-hidden="true">
                          error
                        </span>
                        <span className="font-serif text-editorial-black">{status.failed} failed</span>
                      </div>
                      <span
                        className={`material-symbols-outlined text-gray-400 transition-transform ${
                          isErrorsExpanded ? 'rotate-180' : ''
                        }`}
                        style={{ fontSize: 16 }}
                        aria-hidden="true"
                      >
                        expand_more
                      </span>
                    </button>

                    {isErrorsExpanded && (
                      <ul
                        id="error-list"
                        className="mt-2 space-y-2 pl-7 text-sm"
                        role="list"
                      >
                        {status.errors.map((err) => (
                          <li key={err.plantId} className="font-serif text-gray-500">
                            <span className="font-medium text-editorial-black">
                              {err.plantName}
                            </span>
                            : {err.error}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Action buttons */}
        <div className="mt-4 flex flex-wrap gap-2">
          {status.state === 'idle' && (
            <button
              onClick={handleStart}
              className="h-12 rounded-lg bg-editorial-black px-6 font-sans font-bold text-xs uppercase tracking-widest text-white transition-colors hover:bg-black focus:outline-none focus-visible:ring-2 focus-visible:ring-editorial-black focus-visible:ring-offset-2"
            >
              Start Upgrade
            </button>
          )}

          {status.state === 'running' && (
            <>
              <button
                onClick={handlePause}
                className="h-12 rounded-lg border border-gray-300 bg-white px-6 font-sans font-bold text-xs uppercase tracking-widest text-editorial-black transition-colors hover:border-gray-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-editorial-black focus-visible:ring-offset-2"
              >
                Pause
              </button>
              <button
                onClick={handleCancel}
                className="h-12 rounded-lg border border-gray-300 bg-white px-6 font-sans font-bold text-xs uppercase tracking-widest text-editorial-black transition-colors hover:border-gray-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-editorial-black focus-visible:ring-offset-2"
              >
                Cancel
              </button>
            </>
          )}

          {status.state === 'paused' && !status.rateLimitResetAt && (
            <>
              <button
                onClick={handleResume}
                className="h-12 rounded-lg bg-editorial-black px-6 font-sans font-bold text-xs uppercase tracking-widest text-white transition-colors hover:bg-black focus:outline-none focus-visible:ring-2 focus-visible:ring-editorial-black focus-visible:ring-offset-2"
              >
                Resume
              </button>
              <button
                onClick={handleCancel}
                className="h-12 rounded-lg border border-gray-300 bg-white px-6 font-sans font-bold text-xs uppercase tracking-widest text-editorial-black transition-colors hover:border-gray-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-editorial-black focus-visible:ring-offset-2"
              >
                Cancel
              </button>
            </>
          )}

          {status.state === 'paused' && status.rateLimitResetAt && (
            <button
              onClick={handleCancel}
              className="h-12 rounded-lg border border-gray-300 bg-white px-6 font-sans font-bold text-xs uppercase tracking-widest text-editorial-black transition-colors hover:border-gray-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-editorial-black focus-visible:ring-offset-2"
            >
              Cancel
            </button>
          )}

          {status.state === 'completed' && (
            <>
              {status.failed > 0 && (
                <button
                  onClick={handleRetryFailed}
                  className="h-12 rounded-lg bg-editorial-black px-6 font-sans font-bold text-xs uppercase tracking-widest text-white transition-colors hover:bg-black focus:outline-none focus-visible:ring-2 focus-visible:ring-editorial-black focus-visible:ring-offset-2"
                >
                  Retry Failed
                </button>
              )}
              <button
                onClick={handleDismiss}
                className="h-12 rounded-lg border border-gray-300 bg-white px-6 font-sans font-bold text-xs uppercase tracking-widest text-editorial-black transition-colors hover:border-gray-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-editorial-black focus-visible:ring-offset-2"
              >
                Done
              </button>
            </>
          )}
        </div>
      </div>
    </section>
  );
}
